/**
 * The auth gate.
 *
 * One question decides everything: does /auth/me return a user? The SPA never
 * decides who you are — it asks the API, which reads an httpOnly cookie that
 * JavaScript cannot see. There is no token in localStorage and no client-side
 * "is logged in" flag that could disagree with the server.
 *
 * Replaces the Phase 0 wiring placeholder, whose job (prove the SPA reaches the
 * API cross-origin with credentials and shares @inventory-tracker/core) is now
 * done rather more convincingly by signing in.
 */
import { LegalPage, type LegalDoc } from './legal/LegalPage.js';
import { useCallback, useEffect, useState } from 'react';
import { getMe, type Me } from './lib/api.js';
import { AuthScreen } from './auth/AuthScreen.js';
import { LandingPage } from './auth/LandingPage.js';
import { ForgotPasswordScreen } from './auth/ForgotPasswordScreen.js';
import { ResetPasswordScreen } from './auth/ResetPasswordScreen.js';
import { UnsubscribeScreen } from './auth/UnsubscribeScreen.js';
import { VerifyEmailScreen } from './auth/VerifyEmailScreen.js';
import { ActivateScreen } from './auth/ActivateScreen.js';
import { BillingDoneScreen } from './app/BillingDoneScreen.js';
import { AppShell } from './app/AppShell.js';
import { c, display, sans } from './theme.js';

type State =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; me: Me }
  | { status: 'unreachable'; message: string };

/**
 * Two routes exist, both entered from an emailed link:
 *   /reset-password?token=...
 *   /verify-email?token=...
 *   /unsubscribe?token=...   (the link in an expiry digest; works signed out)
 *   /billing/done            (Stripe returns here after Checkout)
 *
 * Read straight off `location` rather than pulling in a router. Two paths and no
 * in-app navigation does not justify the dependency (CLAUDE.md on dependencies);
 * when the sidebar arrives in Phase 2 and there are real routes to manage, that
 * calculation changes and a router is the right answer then.
 */
type Route =
  | { name: 'app' }
  | { name: 'forgot' }
  | { name: 'reset'; token: string }
  | { name: 'verify'; token: string }
  | { name: 'activate'; token: string }
  | { name: 'unsubscribe'; token: string }
  | { name: 'billingDone' }
  | { name: 'legal'; doc: LegalDoc };

function routeFromLocation(): Route {
  const { pathname, searchParams } = new URL(window.location.href);
  const token = searchParams.get('token') ?? '';

  // A token-bearing path with no token is a mangled link — a mail client that
  // truncated the URL, usually. Fall through to the app rather than render a
  // form that cannot possibly succeed.
  if (pathname === '/reset-password' && token) return { name: 'reset', token };
  if (pathname === '/verify-email' && token) return { name: 'verify', token };
  if (pathname === '/activate' && token) return { name: 'activate', token };
  if (pathname === '/unsubscribe' && token) return { name: 'unsubscribe', token };
  // Stripe sends the customer here after Checkout. No token to validate: the
  // payment is confirmed by the webhook, not by anything in this URL.
  if (pathname === '/billing/done') return { name: 'billingDone' };
  // Linked from the landing page and sign-up; readable signed in or out.
  if (pathname === '/terms') return { name: 'legal', doc: 'terms' };
  if (pathname === '/privacy') return { name: 'legal', doc: 'privacy' };
  return { name: 'app' };
}

export function App() {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [route, setRoute] = useState<Route>(routeFromLocation);
  /**
   * What a signed-out visitor sees: the landing page, or the form on one of its
   * two tabs. Held in state, not the URL — there is no router, and a refresh
   * landing back on the landing page is the right behaviour for a visitor.
   */
  const [authView, setAuthView] = useState<'landing' | 'login' | 'signup'>('landing');

  /**
   * Clears the path and the token from the address bar. Leaving a spent token in
   * history means it shows up in shared screenshots and browser sync, and a
   * refresh would retry a link that is already consumed.
   */
  const goToApp = useCallback(() => {
    window.history.replaceState({}, '', '/');
    setRoute({ name: 'app' });
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await getMe();
      setState(me ? { status: 'signed-in', me } : { status: 'signed-out' });
    } catch (err) {
      // A failure here means the API is unreachable, not that the visitor is
      // signed out — getMe() already treats 401 as a normal "no". Showing the
      // login form instead would be a lie, and they would type a password into
      // a form that cannot possibly work.
      setState({
        status: 'unreachable',
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Token routes come first and do not depend on being signed in: a password
  // reset is precisely the case where you cannot sign in, and a verification link
  // may be opened on a different device from the one that signed up.
  if (route.name === 'reset') {
    return (
      <ResetPasswordScreen
        token={route.token}
        onDone={() => {
          goToApp();
          void refresh();
        }}
      />
    );
  }

  if (route.name === 'activate') {
    return (
      <ActivateScreen
        token={route.token}
        onActivated={() => {
          goToApp();
          void refresh();
        }}
      />
    );
  }

  if (route.name === 'legal') {
    return <LegalPage doc={route.doc} onHome={goToApp} />;
  }

  if (route.name === 'billingDone') {
    return (
      <BillingDoneScreen
        previous={state.status === 'signed-in' ? state.me : null}
        onContinue={() => {
          goToApp();
          void refresh();
        }}
      />
    );
  }

  if (route.name === 'unsubscribe') {
    return (
      <UnsubscribeScreen
        token={route.token}
        onContinue={() => {
          goToApp();
          void refresh();
        }}
      />
    );
  }

  if (route.name === 'verify') {
    return (
      <VerifyEmailScreen
        token={route.token}
        onContinue={() => {
          goToApp();
          void refresh();
        }}
      />
    );
  }

  if (state.status === 'loading') return <Splash />;
  if (state.status === 'unreachable') {
    return <Unreachable message={state.message} onRetry={refresh} />;
  }

  if (state.status === 'signed-out') {
    if (route.name === 'forgot') {
      return <ForgotPasswordScreen onBack={() => setRoute({ name: 'app' })} />;
    }
    if (authView === 'landing') {
      return (
        <LandingPage onLogin={() => setAuthView('login')} onSignup={() => setAuthView('signup')} />
      );
    }
    return (
      <AuthScreen
        // Remount when the landing page picks the other tab, so the form opens on it.
        key={authView}
        initialMode={authView}
        onAuthenticated={refresh}
        onForgotPassword={() => setRoute({ name: 'forgot' })}
        onHome={(toPricing) => {
          setAuthView('landing');
          if (toPricing) {
            // After the landing page has rendered; there is nothing to scroll to before.
            requestAnimationFrame(() =>
              document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }),
            );
          }
        }}
      />
    );
  }

  return <AppShell me={state.me} onSignedOut={refresh} onAccountChanged={refresh} />;
}

function Splash() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: c.authBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: sans,
        color: c.muted,
        fontSize: 14,
      }}
    >
      Loading…
    </div>
  );
}

function Unreachable({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: c.authBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: sans,
      }}
    >
      <div style={{ maxWidth: 420, textAlign: 'center' }}>
        <h1 style={{ ...display, fontSize: 34, fontWeight: 300, margin: '0 0 10px' }}>
          Can’t reach the server
        </h1>
        <p style={{ fontSize: 14, color: c.muted, lineHeight: 1.6, margin: '0 0 6px' }}>
          The app is running but the API isn’t answering.
        </p>
        <p style={{ fontSize: 12.5, color: c.faint, margin: '0 0 22px', lineHeight: 1.6 }}>
          Locally that usually means the API isn’t running, or Postgres isn’t up. Try{' '}
          <code>pnpm db:up</code> then <code>pnpm dev</code>.
          <br />
          Details: {message}
        </p>
        <button
          type="button"
          onClick={onRetry}
          style={{
            background: c.green,
            border: 'none',
            color: c.white,
            fontSize: 15,
            fontWeight: 500,
            fontFamily: sans,
            padding: '12px 20px',
            borderRadius: 14,
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </div>
    </div>
  );
}
